import React, { useEffect, useState } from "react";
import { Space, Tag } from "antd";
import { EditOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import DataTable from "../../../components/Comman/CustomTable/CommonTable";
import { useDispatch, useSelector } from "react-redux";
import CategoryStatistics from "../../../utils/Carousel";
import AdminDashboardDetails from "../../../components/Comman/AdminDashboard/AdminDashboardDetails";
import { getAdminDashboardData } from "../../../actions/admin";
import SideDrawer from "../../../components/Comman/Drawer/SideDrawer";
import userProfile from "../../../assets/img/profile-icon.svg";
import {
  submissionListAction,
  updateDataEntryForm,
} from "../../../actions/user";
import { formatDateToCST, formatSubmissionValue } from "../../../utils/comman";
import { MailOutlined } from "@ant-design/icons";
import ConfirmDeleteModal from "../../../components/Comman/ConfirmDeleteModal/ConfirmDeleteModal";
import notificationIcon from "../../../assets/img/notification-icon.svg";

const UserDashboard = () => {
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<any>({});
  const [isModalOpen, setIsModelOpen] = useState<boolean>(false);
  const [unlockingId, setUnlockingId] = useState<string>(null);
  const user = useSelector((state: any) => state?.Auth?.user);
  const roleId = user?.user?.roleId;
  const profilePicture = user?.user?.profilePhoto;
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [isDataEntryModelOpen, setIsDataEntryModelOpen] =
    useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({
    field: null,
    order: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 4,
    total: 0,
  });
  const handleRefresh = useSelector(
    (state: any) => state?.General?.updateListing
  );

  // useEffect to trigger API call when pagination state changes
  useEffect(() => {
    fetchSubmissionList({
      page: pagination.current, // Current page number
      limit: pagination.pageSize, // Number of records per page
    });
  }, [handleRefresh]); // Dependency array ensures function re-runs on pagination updates

  const handleEditClick = (record: any) => {
    handleIsDataEntryModelOpen();
    setSelectedEntry(record); // Store the selected organization's data
  };

  const handleIsDataEntryModelOpen: any = () => {
    setIsDataEntryModelOpen(!isDataEntryModelOpen);
  };

  const handleModelState = (id?: any) => {
    if (id) setUnlockingId(id); // Store the selected user ID
    setIsModelOpen(!isModalOpen);
  };

  // Handles table pagination change event
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let newSortConfig = { ...sortConfig };
    if (sorter && Object.keys(sorter).length > 0) {
      newSortConfig = {
        field: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      };
    }
    setSortConfig(newSortConfig);
    fetchSubmissionList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 4,
      ...(newSortConfig.field && newSortConfig.order
        ? {
            sort: newSortConfig.field,
            order: newSortConfig.order,
          }
        : {}),
    });
  };

  // Fetches admin dashboard data asynchronously
  const getDashboardData = async () => {
    try {
      const response: any = await dispatch(getAdminDashboardData(roleId)); // ✅ Dispatch and wait for response
      setDashboardData(response?.data); // ✅ Now `data` exists
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []); // Empty dependency array ensures this runs only once on mount

  //My submission list
  // Fetches the list of organizations with optional query parameters
  const fetchSubmissionList = (params = {}) => {
    setLoading(true); // Show loading indicator before making the API call
    const requestParams = {
      params,
      currentMonth: 1,
    };
    dispatch(
      submissionListAction(requestParams, (resp: any) => {
        setLoading(false); // Hide loading indicator once the API call is completed
        if (resp?.status === 200) {
          setData(resp?.data || []); // Store the fetched organization data

          // Update pagination state based on API response
          setPagination({
            ...pagination,
            total: resp.pagination?.totalItems,
            current: resp.pagination?.currentPage,
            pageSize: resp.pagination?.pageSize,
          });
        }
      })
    );
  };

  const getRowClassName = (record: any) => {
    return record?.SubmissionData_Status === "submitted" ? "faded-row" : "";
  };

  const handleReportUnlocking = () => {
    const payload = {
      SubmissionData_Update_Request: true,
      id: unlockingId,
    };
    dispatch(
      updateDataEntryForm(payload, (resp: any) => {
        handleModelState();
      })
    );
  };

  const columns = [
    {
      title: "Org",
      dataIndex: "organizationName",
      key: "organization",
      render: (_, data) => data?.organization?.shortCode || "-",
    },
    {
      title: "DE",
      dataIndex: "dataElement",
      key: "dataElement",
      render: (dataElement: any) => dataElement?.DataElements_Name,
    },
    {
      title: "Category",
      dataIndex: "dataElement",
      key: "dataElement.category.id",
      render: (dataElement: any) => dataElement?.category?.name || "-",
    },
    {
      title: "Submission Data Year",
      dataIndex: "SubmissionData_Year",
      key: "SubmissionData_Year",
    },
    {
      title: "Submission Data Period",
      dataIndex: "SubmissionData_Period",
      key: "SubmissionData_Period",
      render: (_, record) =>
        record.SubmissionData_Period
          ? `${record.SubmissionData_IntervalType} ${
              record.SubmissionData_Period || "N/A"
            }`
          : "-",
    },
    {
      title: "Submission Data Value",
      dataIndex: "SubmissionData_Value",
      key: "SubmissionData_Value",
      render: (value: string, record: any) => {
        const formattedValue = formatSubmissionValue(
          value,
          record?.dataElement?.DataElements_DataType
        );

        return (
          <div
            className={formattedValue === "-" ? "text-center" : "text-right"}
          >
            {formattedValue}
          </div>
        );
      },
    },

    {
      title: "Submission Due On",
      dataIndex: "SubmissionData_Updated",
      key: "SubmissionData_Updated",
      render: (value) => formatDateToCST(value),
    },
    {
      title: "Status",
      key: "status",
      render: (record: any) => (
        <>
          {record.SubmissionData_Status === "submitted" ? (
            <Tag color="green">Submitted</Tag>
          ) : record.SubmissionData_Status === "past due" ? (
            <Tag color="red">Past Due</Tag>
          ) : record.SubmissionData_Status === "pending" ? (
            <Tag color="yellow">Pending</Tag>
          ) : (
            <Tag color="default">Unknown</Tag> // Default case for unexpected values
          )}
        </>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "20px" }}
          >
            <EditOutlined
              onClick={() => {
                if (record?.SubmissionData_Status !== "submitted")
                  handleEditClick(record);
              }}
            />
          </span>
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "20px" }}
          >
            {record.SubmissionData_Status === "submitted" ? (
              <LockOutlined onClick={() => handleModelState(record?.id)} />
            ) : (
              <UnlockOutlined />
            )}
          </span>
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "20px" }}
          >
            <MailOutlined />
          </span>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* <Header /> */}

      {/* <HeaderMenu /> */}

      <div className="flex flex-wrap gap-[22px] mt-2">
        <div className=" w-full lg:w-[calc(75%-11px)]  ">
          <AdminDashboardDetails data={dashboardData} />

          <CategoryStatistics />
        </div>

        {/* <AdminDashboardSocialLinks graphData={dashboardData} /> */}

        <div className="w-full lg:w-[calc(25%-11px)]">
          <div className="user-profile-col-wrapper bg-[#10151A] rounded-[10px] px-[16px] 2xl:px-[20px] pt-[20px] pb-40 2xl:pt-[38px] 2xl:pb-[60px] mb-[20px]">
            <div className="flex gap-[14px] items-center">
              <div className="inline-flex p-1 border border-dashed border-[#A2A2A2] bg-dark rounded-full size-[54px] relative">
                <div className="inline-flex items-center justify-center p-2 bg-[#D9D9D9] size-[45px] min-w-[45px] rounded-full overflow-hidden">
                  <img
                    src={profilePicture ? profilePicture : userProfile}
                    alt="profile-icon"
                    className="max-w-[65px] h-[65px] object-cover"
                  />
                </div>

                <span className="rounded-full bg-[#51D15E] size-[15px] border border2 border-[#000] absolute bottom-[3px] right-[5px]"></span>
              </div>

              <div>
                <h4 className="text-sm 2xl:text-[16px]">
                  {dashboardData?.name}
                </h4>
                <span className="user-email-col text-white break-all text-[12px] 2xl:text-[14px]">
                  User ID: {dashboardData?.id}
                </span>
              </div>
            </div>

            <span className="profile-bottom-line block h-px mt-[35px]"> </span>

            <div className="flex justify-center">
              <div className="w-[calc(50%-11px)] text-center custom-border pt-[15px]">
                <span className="user-email-col text-white break-all text-[12px] 2xl:text-[14px]">
                  Created:
                </span>
                <h4 className="text-[12px] 2xl:text-[14px]">28 NOV 2024</h4>
              </div>

              <div className="w-[calc(50%-11px)] text-center pt-[15px]">
                <span className="user-email-col text-white break-all text-[12px] 2xl:text-[14px]">
                  Last Login
                </span>
                <h4 className="text-[12px] 2xl:text-[14px]">20 JAN 2025</h4>
              </div>
            </div>
          </div>
          <div className="notification-col-wrapper flex gap-[15px] items-center rounded-[15px] py-[20px] px-[12px] bg-[#10151A]">
            <div>
              <img src={notificationIcon} alt="icon" className="w-full" />
            </div>

            <div>
              {/* <h4 className="text-[12px] mb-[7px]">Dear John,</h4> */}
              <p className="text-[12px] break-words ">
                There are no notifications for you today.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[20px]">
        <div className="table-col rounded-[12px] bg-[#10151A]">
          <h3 className=" text-[12px] 2xl:text-sm text-[#919191] px-[20px] py-[15px]">
            Current Period Data Submissions
          </h3>
          <DataTable
            rowClassName={getRowClassName} // Apply row styling conditionally
            columns={columns}
            data={data}
            loading={false}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize || 4,
              total: pagination.total,
            }}
            onTableChange={handleTableChange}
          />
        </div>
      </div>

      <SideDrawer
        open={isDataEntryModelOpen}
        onClose={handleIsDataEntryModelOpen}
        type={"dataEntry"}
        organizationData={selectedEntry} // Pass only selected record
      />

      {isModalOpen && (
        <ConfirmDeleteModal
          handleConfirmDeleteModel={handleModelState}
          type="reportUnlock"
          handleModelFunction={handleReportUnlocking}
        />
      )}
    </div>
  );
};

export default UserDashboard;
